// @ts-nocheck
import React, { useState, useEffect } from 'react'
import { useOutboundCall } from '../hooks/useOutboundCall'
import { useCustomerSearch } from '../hooks/useCustomerSearch'
import { useCallFormatters } from '../hooks/useCallFormatters'
import { X, Phone, Search, User } from 'lucide-react'

/**
 * アウトバウンドコール発信ダイアログ
 * 
 * @param {boolean} isOpen - ダイアログの表示状態
 * @param {Function} onClose - ダイアログを閉じる関数
 * @param {string} defaultNumber - デフォルトの電話番号（オプション）
 * @param {Object} callbackCallHistory - コールバック元の通話履歴（オプション）
 */
export const OutboundCallDialog = ({
  isOpen,
  onClose,
  defaultNumber = '',
  callbackCallHistory = null
}) => {
  const [activeTab, setActiveTab] = useState('phone') // 'phone' | 'customer'
  const [phoneNumber, setPhoneNumber] = useState(defaultNumber)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [callerId, setCallerId] = useState('')
  const [phoneNumbers, setPhoneNumbers] = useState([])
  const [isLoadingNumbers, setIsLoadingNumbers] = useState(false)
  
  const {
    makeOutboundCall,
    makeCallbackCall,
    fetchPhoneNumbers,
    isDialing,
    error,
    clearError
  } = useOutboundCall()
  
  const {
    searchQuery,
    setSearchQuery,
    customers,
    isSearching,
    error: searchError,
    clearSearch,
  } = useCustomerSearch()
  
  const { formatPhoneNumber } = useCallFormatters()

  // ダイアログが開かれたときに電話番号一覧を取得
  useEffect(() => {
    if (isOpen) {
      loadPhoneNumbers()
      setPhoneNumber(defaultNumber)
      
      // コールバック発信の場合は電話番号タブを表示
      if (callbackCallHistory) {
        setActiveTab('phone')
      }
    } else {
      // ダイアログが閉じられたらリセット
      clearError()
      clearSearch()
      setSelectedCustomer(null)
      setActiveTab('phone')
    }
  }, [isOpen, defaultNumber, callbackCallHistory])

  const loadPhoneNumbers = async () => {
    setIsLoadingNumbers(true)
    try {
      const numbers = await fetchPhoneNumbers()
      setPhoneNumbers(numbers)
      
      // デフォルトの発信者番号を設定
      if (callbackCallHistory) {
        // コールバックの場合: 着信番号を発信者番号として使用
        const defaultCallerId = callbackCallHistory.originalDirection === 'inbound'
          ? callbackCallHistory.to
          : callbackCallHistory.from
        setCallerId(defaultCallerId)
      } else {
        // 新規発信の場合: 最初の番号を使用
        if (numbers.length > 0) {
          setCallerId(numbers[0].phoneNumber)
        }
      }
    } finally {
      setIsLoadingNumbers(false)
    }
  }

  // 電話番号を正規化（E.164形式に変換）
  const normalizePhoneNumber = (phone) => {
    if (!phone) return phone
    
    // すでに+で始まっている場合はそのまま
    if (phone.startsWith('+')) {
      return phone.replace(/[^\d+]/g, '')
    }
    
    // 数字のみを抽出
    const cleaned = phone.replace(/[^\d]/g, '')
    
    // 0で始まる場合は+81に変換
    if (cleaned.startsWith('0')) {
      return '+81' + cleaned.substring(1)
    }
    
    // 81で始まる場合は+を追加
    if (cleaned.startsWith('81')) {
      return '+' + cleaned
    }
    
    // その他の場合は+81を付加
    return '+81' + cleaned
  }

  const handleCall = async () => {
    let success = false
    
    if (callbackCallHistory) {
      // コールバック発信
      success = await makeCallbackCall(callbackCallHistory)
    } else if (activeTab === 'customer' && selectedCustomer) {
      // 顧客から発信（顧客の電話番号は既に正規化済み）
      success = await makeOutboundCall(selectedCustomer.phoneNumber, callerId)
    } else {
      // 電話番号から発信（入力された番号を正規化）
      const normalizedPhone = normalizePhoneNumber(phoneNumber)
      success = await makeOutboundCall(normalizedPhone, callerId)
    }
    
    if (success) {
      onClose()
    }
  }

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer)
  }

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value
    // 数字、+、-、スペース、括弧のみ許可
    const sanitized = value.replace(/[^\d+\-\s()]/g, '')
    setPhoneNumber(sanitized)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* オーバーレイ */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* ダイアログ */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {/* ヘッダー */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {callbackCallHistory ? '折り返し発信' : '外線発信'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={isDialing}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* タブナビゲーション（コールバック発信時は非表示） */}
          {!callbackCallHistory && (
            <div className="flex border-b border-gray-200 mb-4">
              <button
                onClick={() => setActiveTab('phone')}
                className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'phone'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                disabled={isDialing}
              >
                <div className="flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" />
                  電話番号入力
                </div>
              </button>
              <button
                onClick={() => setActiveTab('customer')}
                className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'customer'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                disabled={isDialing}
              >
                <div className="flex items-center justify-center gap-2">
                  <User className="w-4 h-4" />
                  顧客検索
                </div>
              </button>
            </div>
          )}

          {/* エラーメッセージ */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* コールバック情報 */}
          {callbackCallHistory && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <span className="font-medium">発信先:</span> {
                  formatPhoneNumber(
                    callbackCallHistory.originalDirection === 'inbound'
                      ? callbackCallHistory.from
                      : callbackCallHistory.to
                  )
                }
              </p>
              {callbackCallHistory.customerName && (
                <p className="text-sm text-blue-800 mt-1">
                  <span className="font-medium">顧客:</span> {callbackCallHistory.customerName}
                </p>
              )}
            </div>
          )}

          {/* 電話番号入力タブ */}
          {!callbackCallHistory && activeTab === 'phone' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                発信先電話番号
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                placeholder="090-1234-5678"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isDialing}
                autoFocus
              />
            </div>
          )}

          {/* 顧客検索タブ */}
          {!callbackCallHistory && activeTab === 'customer' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                顧客名または電話番号で検索
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="顧客名、電話番号、会社名で検索..."
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isDialing}
                  autoFocus
                />
                <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>

              {/* 検索エラー */}
              {searchError && (
                <div className="mt-2 text-sm text-red-600">
                  {searchError}
                </div>
              )}

              {/* 検索結果 */}
              <div className="mt-3 max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                {isSearching ? (
                  <div className="p-4 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2" />
                    検索中...
                  </div>
                ) : searchQuery.trim() && customers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    該当する顧客が見つかりませんでした
                  </div>
                ) : searchQuery.trim() ? (
                  <div className="divide-y divide-gray-200">
                    {customers.map((customer) => (
                      <button
                        key={customer.customerId}
                        onClick={() => handleCustomerSelect(customer)}
                        className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                          selectedCustomer?.customerId === customer.customerId
                            ? 'bg-blue-50 border-l-4 border-blue-500'
                            : ''
                        }`}
                        disabled={isDialing}
                      >
                        <div className="font-medium text-gray-900">
                          {customer.name || '名前未登録'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatPhoneNumber(customer.phoneNumber)}
                        </div>
                        {customer.companyName && (
                          <div className="text-sm text-gray-500">
                            {customer.companyName}
                          </div>
                        )}
                        {customer.stats?.lastCallAt && (
                          <div className="text-xs text-gray-400 mt-1">
                            最終通話: {new Date(customer.stats.lastCallAt).toLocaleDateString('ja-JP')}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-400 text-sm">
                    顧客名または電話番号を入力してください
                  </div>
                )}
              </div>

              {/* 選択中の顧客 */}
              {selectedCustomer && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="text-sm text-blue-800">
                    <span className="font-medium">選択中:</span> {selectedCustomer.name || '名前未登録'}
                  </div>
                  <div className="text-sm text-blue-700">
                    {formatPhoneNumber(selectedCustomer.phoneNumber)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 発信者番号選択 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              発信者番号
            </label>
            {isLoadingNumbers ? (
              <div className="text-sm text-gray-500">読み込み中...</div>
            ) : phoneNumbers.length === 0 ? (
              <div className="text-sm text-red-600">
                利用可能な電話番号がありません
              </div>
            ) : (
              <select
                value={callerId}
                onChange={(e) => setCallerId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isDialing}
              >
                {phoneNumbers.map((number) => (
                  <option key={number.phoneNumberId} value={number.phoneNumber}>
                    {number.name} ({formatPhoneNumber(number.phoneNumber)})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* アクションボタン */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              disabled={isDialing}
            >
              キャンセル
            </button>
            <button
              onClick={handleCall}
              disabled={
                isDialing ||
                (!callbackCallHistory && activeTab === 'phone' && !phoneNumber) ||
                (!callbackCallHistory && activeTab === 'customer' && !selectedCustomer) ||
                !callerId ||
                phoneNumbers.length === 0
              }
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isDialing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  発信中...
                </>
              ) : (
                <>
                  <Phone className="h-5 w-5" />
                  発信
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
